// Journal.js — Session reflection journal with admin feedback
import React, { useState, useEffect } from "react";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";

// ─── CLIENT: Journal entry box inside session modal ───────────────────────────
export function JournalEntry({ sessionId, userId, sessionLabel, dayNum, isAdmin = false, clientName = "" }) {
  const [entry, setEntry] = useState(null);
  const [text, setText] = useState("");
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const docRef = doc(db, "journals", userId, "entries", sessionId);

  useEffect(() => {
    const unsub = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setEntry(data);
        setText(data.text || "");
        setFeedback(data.feedback || "");
      } else {
        setEntry(null);
        setText("");
        setFeedback("");
      }
    });
    return unsub;
  }, [sessionId, userId]);

  const saveEntry = async () => {
    if (!text.trim()) return;
    setSaving(true);
    await setDoc(docRef, {
      text, sessionId, sessionLabel, dayNum, userId,
      updatedAt: new Date().toISOString(),
      createdAt: entry?.createdAt || new Date().toISOString(),
      feedback: entry?.feedback || "",
      feedbackAt: entry?.feedbackAt || null,
    }, { merge: true });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const saveFeedback = async () => {
    if (!feedback.trim()) return;
    setSavingFeedback(true);
    await setDoc(docRef, {
      feedback,
      feedbackAt: new Date().toISOString(),
    }, { merge: true });
    setSavingFeedback(false);
    setSavedFeedback(true);
    setTimeout(() => setSavedFeedback(false), 2000);
  };

  // ADMIN VIEW
  if (isAdmin) {
    return (
      <div className="admin-journal-wrap">
        <style>{JOURNAL_CSS}</style>
        {!entry?.text ? (
          <p className="no-entry">No reflection written for this session yet.</p>
        ) : (
          <>
            <div className="client-entry-box">
              <div className="entry-header">
                <span className="entry-label">{clientName}'s reflection</span>
                <span className="entry-date">{new Date(entry.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              <p className="entry-text">{entry.text}</p>
            </div>
            <div className="feedback-wrap">
              <label className="feedback-label">Your response</label>
              <textarea className="feedback-textarea" value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Write your coaching response here…" rows={3} />
              <button className="feedback-send-btn" onClick={saveFeedback} disabled={savingFeedback || !feedback.trim()}>
                {savingFeedback ? "Sending…" : savedFeedback ? "✓ Sent" : entry?.feedback ? "Update Response" : "Send Response"}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // CLIENT VIEW
  return (
    <div className="journal-wrap">
      <style>{JOURNAL_CSS}</style>
      <button className="journal-toggle" onClick={() => setExpanded(!expanded)}>
        <div className="journal-toggle-left">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#4F9CA8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
          <div>
            <span className="journal-toggle-title">Session Reflection</span>
            <span className="journal-toggle-sub">
              {entry?.text ? "You wrote a reflection" : "Optional — write how you feel"}
              {entry?.feedback && " · Coach replied"}
            </span>
          </div>
        </div>
        <svg className={`journal-chevron ${expanded ? "open" : ""}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
          <polyline points="4 6 8 10 12 6"/>
        </svg>
      </button>

      {expanded && (
        <div className="journal-body">
          <p className="journal-prompt">How did this session feel? What came up for you? This is your private reflection — optionally shared with your coach.</p>
          <textarea className="journal-textarea" value={text} onChange={e => setText(e.target.value)} placeholder="Write your reflection here…" rows={4} />
          <div className="journal-actions">
            <button className="journal-save-btn" onClick={saveEntry} disabled={saving || !text.trim()}>
              {saving ? "Saving…" : saved ? "✓ Saved" : "Save Reflection"}
            </button>
            {entry?.text && <span className="journal-saved-at">Last saved {new Date(entry.updatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
          </div>
          {entry?.feedback && (
            <div className="coach-feedback">
              <div className="coach-feedback-header">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#3E7D6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                <strong>Coach's Response</strong>
                {entry.feedbackAt && <span className="feedback-date">{new Date(entry.feedbackAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>}
              </div>
              <p className="coach-feedback-text">{entry.feedback}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ADMIN: All journals for one client ───────────────────────────────────────
export function ClientJournals({ client, program }) {
  const [journals, setJournals] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { doc, getDoc } = await import("firebase/firestore");
        const allSessions = program.flatMap(d => d.sessions);
        const result = {};
        for (const s of allSessions) {
          const snap = await getDoc(doc(db, "journals", client.id, "entries", s.id));
          if (snap.exists()) result[s.id] = snap.data();
        }
        setJournals(result);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [client.id, program]);

  const entriesCount = Object.keys(journals).length;

  if (loading) return <div className="journal-loading"><style>{JOURNAL_CSS}</style>Loading journals…</div>;

  return (
    <div className="client-journals">
      <style>{JOURNAL_CSS}</style>
      <div className="journals-summary">
        <span className="journals-count">{entriesCount} reflection{entriesCount !== 1 ? "s" : ""} written</span>
        {entriesCount === 0 && <p className="no-journals">This client hasn't written any reflections yet.</p>}
      </div>
      {program.map(day => {
        const dayEntries = day.sessions.filter(s => journals[s.id]);
        if (dayEntries.length === 0) return null;
        return (
          <div key={day.day} className="journal-day-group">
            <span className="journal-day-label">Day {day.day}{day.isOrientation ? " · Orientation" : ""}</span>
            {day.sessions.map(session => {
              const entry = journals[session.id];
              if (!entry) return null;
              const isOpen = expandedSession === session.id;
              return (
                <div key={session.id} className={`journal-entry-card ${isOpen ? "open" : ""}`}>
                  <button className="journal-entry-header" onClick={() => setExpandedSession(isOpen ? null : session.id)}>
                    <span className="je-icon">{session.icon}</span>
                    <div className="je-meta">
                      <span className="je-label">{session.label}</span>
                      <span className="je-preview">{entry.text.slice(0, 60)}{entry.text.length > 60 ? "…" : ""}</span>
                    </div>
                    <div className="je-right">
                      {entry.feedback ? <span className="je-replied">Replied</span> : <span className="je-awaiting">Awaiting response</span>}
                      <svg className={`journal-chevron ${isOpen ? "open" : ""}`} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12"><polyline points="4 6 8 10 12 6"/></svg>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="journal-entry-body">
                      <JournalEntry sessionId={session.id} userId={client.id} sessionLabel={session.label} dayNum={day.day} isAdmin={true} clientName={client.name?.split(" ")[0] || "Client"} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

const JOURNAL_CSS = `
  .journal-wrap{border-top:1px solid #D6E2E5;margin-top:16px}
  .journal-toggle{width:100%;background:none;border:none;cursor:pointer;padding:14px 0 10px;display:flex;align-items:center;justify-content:space-between;font-family:'Inter',sans-serif}
  .journal-toggle-left{display:flex;align-items:center;gap:10px}
  .journal-toggle-title{display:block;font-size:.85rem;font-weight:500;color:#1E2E33;text-align:left}
  .journal-toggle-sub{display:block;font-size:.72rem;color:#8FA6AD;text-align:left}
  .journal-chevron{color:#8FA6AD;transition:transform .2s;flex-shrink:0}
  .journal-chevron.open{transform:rotate(180deg)}
  .journal-body{padding-bottom:16px}
  .journal-prompt{font-size:.78rem;color:#5A7480;line-height:1.6;margin-bottom:10px;font-style:italic}
  .journal-textarea{width:100%;padding:12px;border:1px solid #D6E2E5;border-radius:10px;font-family:'Inter',sans-serif;font-size:.85rem;color:#1E2E33;background:white;resize:vertical;line-height:1.6;box-sizing:border-box}
  .journal-textarea:focus{outline:none;border-color:#4F9CA8}
  .journal-textarea::placeholder{color:#8FA6AD}
  .journal-actions{display:flex;align-items:center;gap:12px;margin-top:10px}
  .journal-save-btn{background:#1F4E5A;color:white;border:none;border-radius:8px;padding:8px 18px;font-family:'Inter',sans-serif;font-size:.8rem;font-weight:500;cursor:pointer;transition:background .2s}
  .journal-save-btn:hover:not(:disabled){background:#4F9CA8}
  .journal-save-btn:disabled{opacity:.5;cursor:not-allowed}
  .journal-saved-at{font-size:.7rem;color:#8FA6AD}
  .coach-feedback{background:#EDF5EB;border:1px solid #C4D4BF;border-radius:10px;padding:14px;margin-top:14px}
  .coach-feedback-header{display:flex;align-items:center;gap:8px;margin-bottom:8px}

  .coach-feedback-header strong{font-size:.82rem;color:#3E7D6B;flex:1}
  .feedback-date{font-size:.7rem;color:#6FA89B}
  .coach-feedback-text{font-size:.83rem;color:#1E2E33;line-height:1.6}
  .no-entry{font-size:.78rem;color:#8FA6AD;font-style:italic;padding:8px 0}
  .admin-journal-wrap{padding:4px 0}
  .client-entry-box{background:#F2F6F7;border:1px solid #D6E2E5;border-radius:10px;padding:14px;margin-bottom:12px}
  .entry-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
  .entry-label{font-size:.75rem;font-weight:600;color:#4F9CA8;text-transform:uppercase;letter-spacing:.06em}
  .entry-date{font-size:.7rem;color:#8FA6AD}
  .entry-text{font-size:.85rem;color:#1E2E33;line-height:1.7;white-space:pre-wrap}
  .feedback-wrap{}
  .feedback-label{display:block;font-size:.75rem;font-weight:600;color:#4F9CA8;text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px}
  .feedback-textarea{width:100%;padding:10px 12px;border:1px solid #D6E2E5;border-radius:8px;font-family:'Inter',sans-serif;font-size:.85rem;color:#1E2E33;background:white;resize:vertical;line-height:1.6;box-sizing:border-box}
  .feedback-textarea:focus{outline:none;border-color:#4F9CA8}
  .feedback-send-btn{margin-top:8px;background:#3E7D6B;color:white;border:none;border-radius:8px;padding:8px 18px;font-family:'Inter',sans-serif;font-size:.8rem;font-weight:500;cursor:pointer;transition:background .2s}
  .feedback-send-btn:hover:not(:disabled){background:#6FA89B}
  .feedback-send-btn:disabled{opacity:.5;cursor:not-allowed}
  .client-journals{}
  .journal-loading{font-size:.82rem;color:#8FA6AD;padding:12px 0}
  .journals-summary{margin-bottom:16px}
  .journals-count{font-size:.78rem;color:#5A7480;background:#E3EEF0;padding:4px 12px;border-radius:20px}
  .no-journals{font-size:.82rem;color:#8FA6AD;margin-top:10px;font-style:italic}
  .journal-day-group{margin-bottom:16px}
  .journal-day-label{display:block;font-size:.7rem;font-weight:600;color:#8FA6AD;text-transform:uppercase;letter-spacing:.07em;margin-bottom:6px}
  .journal-entry-card{border:1px solid #D6E2E5;border-radius:10px;overflow:hidden;background:white;margin-bottom:6px}
  .journal-entry-card.open{border-color:#4F9CA8}
  .journal-entry-header{width:100%;background:none;border:none;cursor:pointer;padding:12px 14px;display:flex;align-items:center;gap:10px;font-family:'Inter',sans-serif;transition:background .15s}
  .journal-entry-header:hover{background:#F2F6F7}
  .je-icon{font-size:1rem;flex-shrink:0}
  .je-meta{flex:1;text-align:left}
  .je-label{display:block;font-size:.82rem;font-weight:500;color:#1E2E33}
  .je-preview{display:block;font-size:.72rem;color:#8FA6AD;margin-top:1px}
  .je-right{display:flex;align-items:center;gap:8px;flex-shrink:0}
  .je-replied{font-size:.68rem;background:#D4E8CF;color:#3E7D6B;padding:2px 8px;border-radius:20px}
  .je-awaiting{font-size:.68rem;background:#FEF6E8;color:#4F9CA8;padding:2px 8px;border-radius:20px}
  .journal-entry-body{padding:14px;border-top:1px solid #D6E2E5;background:#FAFAF8}
`;
